import { useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { uploadFile } from '@/lib/upload';
import { apartmentTypes, apartmentQuartiers } from '@/mocks/apartments';

export interface ApartmentRecord {
  id?: string;
  business_id?: string | null;
  owner_id?: string;
  title: string;
  type: string;
  transaction: string;
  city: string;
  quartier: string;
  price: number;
  bedrooms: number;
  bathrooms: number;
  surface: number;
  description: string;
  images: string[];
  amenities: string[];
  available: boolean;
  owner_type: string;
  owner_name: string;
  owner_phone: string;
  reservation: { checkin: string; checkout: string; bookable: boolean };
}

const amenityOptions = [
  'Climatisation',
  'Parking',
  'Groupe électrogène',
  'Balcon',
  'Jardin',
  'Piscine',
  'Cuisine équipée',
  'Gardien',
  'Meublé',
  'Internet fibre',
  'Eau de forage',
  'Sécurité 24h',
];

interface ApartmentFormProps {
  businessId: string | null;
  ownerId: string;
  existing?: ApartmentRecord | null;
  onSaved: () => void;
}

function emptyForm(): ApartmentRecord {
  return {
    title: '',
    type: 'Appartement',
    transaction: 'location',
    city: 'Lomé',
    quartier: 'Agoè',
    price: 0,
    bedrooms: 0,
    bathrooms: 0,
    surface: 0,
    description: '',
    images: [],
    amenities: [],
    available: true,
    owner_type: 'proprietaire',
    owner_name: '',
    owner_phone: '',
    reservation: { checkin: '14:00', checkout: '12:00', bookable: true },
  };
}

function ApartmentForm({ businessId, ownerId, existing, onSaved }: ApartmentFormProps) {
  const [form, setForm] = useState<ApartmentRecord>(() => {
    if (existing) {
      return {
        ...existing,
        reservation: existing.reservation || { checkin: '14:00', checkout: '12:00', bookable: true },
      };
    }
    return emptyForm();
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const imageInputRef = useRef<HTMLInputElement>(null);

  const update = (field: keyof ApartmentRecord, value: string | number | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setStatus('idle');
  };

  const toggleAmenity = (amenity: string) => {
    setForm((prev) => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter((a) => a !== amenity)
        : [...prev.amenities, amenity],
    }));
  };

  const updateReservation = (field: keyof ApartmentRecord['reservation'], value: string | boolean) => {
    setForm((prev) => ({ ...prev, reservation: { ...prev.reservation, [field]: value } }));
  };

  const handleImageUpload = async (files: FileList | null) => {
    if (!files || files.length === 0 || !businessId) return;
    setUploading(true);
    setErrorMsg('');
    try {
      const urls: string[] = [];
      for (let i = 0; i < files.length; i += 1) {
        const url = await uploadFile(files[i], `apartments/${ownerId}`);
        urls.push(url);
      }
      setForm((prev) => ({ ...prev, images: [...prev.images, ...urls] }));
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "Échec de l'upload des images.");
    } finally {
      setUploading(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const removeImage = (index: number) => {
    setForm((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setStatus('error');
      setErrorMsg('Le titre est obligatoire.');
      return;
    }

    setSaving(true);
    setStatus('idle');
    setErrorMsg('');

    const payload = {
      title: form.title.trim(),
      type: form.type,
      transaction: form.transaction,
      city: form.city.trim() || 'Lomé',
      quartier: form.quartier,
      price: Number(form.price) || 0,
      bedrooms: Number(form.bedrooms) || 0,
      bathrooms: Number(form.bathrooms) || 0,
      surface: Number(form.surface) || 0,
      description: form.description.trim() || null,
      images: form.images,
      amenities: form.amenities,
      available: form.available,
      owner_type: form.owner_type,
      owner_name: form.owner_name.trim() || null,
      owner_phone: form.owner_phone.trim() || null,
      reservation: form.reservation,
    };

    let error = null;
    if (existing?.id) {
      const res = await supabase.from('apartments').update(payload).eq('id', existing.id);
      error = res.error;
    } else {
      const res = await supabase.from('apartments').insert({
        ...payload,
        business_id: businessId,
        owner_id: ownerId,
      });
      error = res.error;
    }

    setSaving(false);

    if (error) {
      setStatus('error');
      setErrorMsg(error.message);
      return;
    }

    onSaved();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Informations principales */}
      <section className="p-6 rounded-xl bg-background-100 border border-background-200/70 space-y-5">
        <h2 className="font-heading font-semibold text-lg text-foreground-50 flex items-center gap-2">
          <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500/10">
            <i className="ri-home-4-line text-primary-400" />
          </span>
          Informations du bien
        </h2>

        <div>
          <label className="block text-sm font-medium text-foreground-400 mb-1.5">Titre *</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => update('title', e.target.value)}
            placeholder="Ex : Appartement 3 chambres à Agoè"
            className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-primary-400"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Type de bien</label>
            <select
              value={form.type}
              onChange={(e) => update('type', e.target.value)}
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 outline-none focus:border-primary-400"
            >
              {apartmentTypes.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Transaction</label>
            <select
              value={form.transaction}
              onChange={(e) => update('transaction', e.target.value)}
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 outline-none focus:border-primary-400"
            >
              <option value="location">À louer</option>
              <option value="vente">À vendre</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Prix (FCFA) *</label>
            <input
              type="number"
              value={form.price || ''}
              onChange={(e) => update('price', Number(e.target.value))}
              placeholder="350000"
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-primary-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Chambres</label>
            <input
              type="number"
              value={form.bedrooms || ''}
              onChange={(e) => update('bedrooms', Number(e.target.value))}
              placeholder="3"
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-primary-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Salles de bain</label>
            <input
              type="number"
              value={form.bathrooms || ''}
              onChange={(e) => update('bathrooms', Number(e.target.value))}
              placeholder="2"
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-primary-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Surface (m²)</label>
            <input
              type="number"
              value={form.surface || ''}
              onChange={(e) => update('surface', Number(e.target.value))}
              placeholder="120"
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-primary-400"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Quartier</label>
            <select
              value={form.quartier}
              onChange={(e) => update('quartier', e.target.value)}
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 outline-none focus:border-primary-400"
            >
              {apartmentQuartiers.map((q) => (
                <option key={q} value={q}>{q}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Ville</label>
            <input
              type="text"
              value={form.city}
              onChange={(e) => update('city', e.target.value)}
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 outline-none focus:border-primary-400"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground-400 mb-1.5">Description</label>
          <textarea
            value={form.description}
            onChange={(e) => update('description', e.target.value)}
            rows={4}
            maxLength={1000}
            placeholder="Décrivez votre bien : pièces, équipements, environnement..."
            className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-primary-400 resize-none"
          />
        </div>
      </section>

      {/* Photos */}
      <section className="p-6 rounded-xl bg-background-100 border border-background-200/70 space-y-5">
        <h2 className="font-heading font-semibold text-lg text-foreground-50 flex items-center gap-2">
          <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-500/10">
            <i className="ri-image-line text-accent-400" />
          </span>
          Photos du bien
        </h2>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {form.images.map((img, idx) => (
            <div key={idx} className="relative rounded-lg overflow-hidden bg-background-50 border border-background-200/70">
              <div className="h-32 w-full overflow-hidden">
                <img src={img} alt="" className="w-full h-full object-cover" />
              </div>
              <button
                type="button"
                onClick={() => removeImage(idx)}
                className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-foreground-950/70 text-foreground-50"
                aria-label="Retirer"
              >
                <i className="ri-close-line" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => imageInputRef.current?.click()}
            disabled={uploading}
            className="h-32 rounded-lg border-2 border-dashed border-background-300/60 flex flex-col items-center justify-center gap-2 text-foreground-400 hover:border-primary-400/40 transition-colors disabled:opacity-50"
          >
            <i className="ri-add-line text-xl" />
            <span className="text-xs">{uploading ? 'Envoi...' : 'Ajouter'}</span>
          </button>
        </div>
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleImageUpload(e.target.files)}
        />
      </section>

      {/* Équipements */}
      <section className="p-6 rounded-xl bg-background-100 border border-background-200/70 space-y-5">
        <h2 className="font-heading font-semibold text-lg text-foreground-50 flex items-center gap-2">
          <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-500/10">
            <i className="ri-checkbox-multiple-line text-secondary-400" />
          </span>
          Équipements
        </h2>
        <div className="flex flex-wrap gap-2">
          {amenityOptions.map((amenity) => (
            <button
              type="button"
              key={amenity}
              onClick={() => toggleAmenity(amenity)}
              className={`px-3 py-2 rounded-full text-xs font-medium border transition-colors whitespace-nowrap ${
                form.amenities.includes(amenity)
                  ? 'bg-primary-500 text-foreground-950 border-primary-500'
                  : 'bg-background-50 text-foreground-500 border-background-300/60 hover:border-primary-400/40'
              }`}
            >
              {amenity}
            </button>
          ))}
        </div>
      </section>

      {/* Réservation */}
      <section className="p-6 rounded-xl bg-background-100 border border-background-200/70 space-y-5">
        <h2 className="font-heading font-semibold text-lg text-foreground-50 flex items-center gap-2">
          <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-500/10">
            <i className="ri-calendar-check-line text-accent-400" />
          </span>
          Réservation
        </h2>
        <p className="text-sm text-foreground-400">
          Indiquez les horaires de visite et d&apos;arrivée. La réservation en ligne se configure via le calendrier intégré.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Heure d&apos;arrivée (check-in)</label>
            <input
              type="time"
              value={form.reservation.checkin}
              onChange={(e) => updateReservation('checkin', e.target.value)}
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 outline-none focus:border-primary-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Heure de départ (check-out)</label>
            <input
              type="time"
              value={form.reservation.checkout}
              onChange={(e) => updateReservation('checkout', e.target.value)}
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 outline-none focus:border-primary-400"
            />
          </div>
        </div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.reservation.bookable}
            onChange={(e) => updateReservation('bookable', e.target.checked)}
            className="w-4 h-4 accent-primary-500"
          />
          <span className="text-sm text-foreground-400">Ce bien accepte la réservation en ligne</span>
        </label>
      </section>

      {/* Coordonnées du bien */}
      <section className="p-6 rounded-xl bg-background-100 border border-background-200/70 space-y-5">
        <h2 className="font-heading font-semibold text-lg text-foreground-50 flex items-center gap-2">
          <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500/10">
            <i className="ri-user-3-line text-primary-400" />
          </span>
          Disponibilité &amp; contact
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Type de contact</label>
            <select
              value={form.owner_type}
              onChange={(e) => update('owner_type', e.target.value)}
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 outline-none focus:border-primary-400"
            >
              <option value="proprietaire">Propriétaire</option>
              <option value="agence">Agence immobilière</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-400 mb-1.5">Téléphone de contact</label>
            <input
              type="tel"
              value={form.owner_phone}
              onChange={(e) => update('owner_phone', e.target.value)}
              placeholder="+228 90 00 00 00"
              className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-primary-400"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-400 mb-1.5">Nom du contact</label>
          <input
            type="text"
            value={form.owner_name}
            onChange={(e) => update('owner_name', e.target.value)}
            placeholder="Votre nom"
            className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-primary-400"
          />
        </div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.available}
            onChange={(e) => update('available', e.target.checked)}
            className="w-4 h-4 accent-primary-500"
          />
          <span className="text-sm text-foreground-400">Bien actuellement disponible</span>
        </label>
      </section>

      {status === 'error' && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400">{errorMsg}</div>
      )}
      {status === 'success' && (
        <div className="p-3 rounded-lg bg-accent-500/10 border border-accent-400/30 text-sm text-accent-400">
          Bien enregistré avec succès.
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving || uploading}
          className="px-6 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors disabled:opacity-60 whitespace-nowrap"
        >
          {saving ? 'Enregistrement...' : existing?.id ? 'Mettre à jour le bien' : 'Publier le bien'}
        </button>
      </div>
    </form>
  );
}

export default ApartmentForm;